import {
  AudioLinesIcon,
  BanIcon,
  BookAudioIcon,
  CheckCheckIcon,
  ChevronDownIcon,
  MicIcon,
  MessageCircleIcon,
  MessageCircleQuestionIcon,
  PhoneIcon,
  VideoIcon,
  TextIcon,
  SquarePenIcon,
  ToggleLeftIcon,
  HandIcon,
  BrainIcon,
  ShieldIcon,
  UserIcon,
  LinkIcon,
  CogIcon,
  HashIcon,
  PercentIcon,
  FileIcon,
  LayersIcon,
  CompassIcon,
  FlaskConicalIcon,
  MonitorPlayIcon,
  AlignLeftIcon,
  ListIcon,
  BookOpenIcon,
  TargetIcon,
  CameraIcon,
  ScanFaceIcon,
  SwitchCameraIcon
} from 'lucide-react';
import {
  CodeDataType,
  CodeSourceTypeEnum,
  ConversationRewardStatus,
  ConversationStatus,
  DeviceType,
  InterviewDeviceSupport,
  InterviewFallbackModality,
  InterviewLanguage,
  InterviewLayout,
  InterviewModality,
  InterviewStatus,
  MediaType,
  MobileCameraSource,
  PanelCampaignStatus,
  PanelType,
  Plan,
  PromptType,
  ReportStyle,
  SubscriptionManagedBy,
  SurveyStatus,
  TopicArchetype,
  TopicType
} from './pulse';

export const TopicTypeMeta = {
  [TopicType.VOICE]: {
    icon: AudioLinesIcon,
    label: 'Conversation',
    description:
      'An open-ended discussion with the user using a defined persona',
    bestFor:
      'exploring experiences, stories, and perspectives to uncover deeper insights or unknowns',
    color: 'pink'
  },
  [TopicType.QUESTION]: {
    icon: MessageCircleQuestionIcon,
    label: 'Question',
    description:
      'A verbatim question asked to the user with specific follow-up probes.',
    bestFor:
      'structured interviews, benchmarking, or quantitative follow-ups where consistency matters',
    color: 'blue'
  },
  [TopicType.STATEMENT]: {
    icon: MessageCircleIcon,
    label: 'Statement',
    description:
      'A statement shared with the user, does not expect a response.',
    bestFor: 'introductions, briefings, or sign-offs',
    color: 'orange'
  },
  [TopicType.SURVEY_TEXT]: {
    icon: TextIcon,
    label: 'Text',
    description: 'A text survey question with free-form text input',
    bestFor: 'open-ended responses or detailed explanations',
    color: 'green'
  },
  [TopicType.SURVEY_MULTI_CHOICE]: {
    icon: CheckCheckIcon,
    label: 'Multi-Choice',
    description: 'A multiple choice survey question with predefined options',
    bestFor: 'categorical data',
    color: 'yellow'
  },
  [TopicType.SURVEY_BOOLEAN]: {
    icon: ToggleLeftIcon,
    label: 'Boolean',
    description: 'A yes/no survey question',
    bestFor: 'simple binary decisions or true/false questions',
    color: 'purple'
  },
  [TopicType.SURVEY_DROPDOWN]: {
    icon: ChevronDownIcon,
    label: 'Dropdown',
    description: 'A dropdown survey question with selectable options',
    bestFor: 'selecting from a long list of options or demographic questions',
    color: 'orange'
  },
  [TopicType.SYSTEM_PROMPT_OVERRIDE]: {
    icon: CogIcon,
    label: 'System Prompt Override',
    description: 'A system prompt override for the topic',
    bestFor: 'complete customization of the topic',
    color: 'red'
  }
};

export const orderedTopicTypes = [
  TopicType.STATEMENT,
  TopicType.VOICE,
  TopicType.QUESTION,
  TopicType.SURVEY_TEXT,
  TopicType.SURVEY_MULTI_CHOICE,
  TopicType.SURVEY_BOOLEAN,
  TopicType.SURVEY_DROPDOWN
];

export const ConversationStatusLabels: Record<ConversationStatus, string> = {
  [ConversationStatus.NOTSTARTED]: 'Not Started',
  [ConversationStatus.PENDING]: 'Pending',
  [ConversationStatus.STARTED]: 'Started',
  [ConversationStatus.PAUSED]: 'Paused',
  [ConversationStatus.COMPLETED]: 'Completed',
  [ConversationStatus.EXITED]: 'Exited',
  [ConversationStatus.ERRORED]: 'Errored',
  [ConversationStatus.FLAGGED]: 'Flagged',
  [ConversationStatus.FAILED_EVAL]: 'Failed Eval',
  [ConversationStatus.SCREENED]: 'Screened',
  [ConversationStatus.SCREENED_OUT]: 'Screened Out'
};

export const getConversationStatusLabel = (
  status: ConversationStatus | null
) => {
  if (!status) return 'Unknown';
  return ConversationStatusLabels[status];
};

export const InterviewStatusLabels: Record<InterviewStatus, string> = {
  [InterviewStatus.DRAFT]: 'Draft',
  [InterviewStatus.LIVE]: 'Live',
  [InterviewStatus.COMPLETE]: 'Closed',
  [InterviewStatus.ARCHIVED]: 'Archived'
};

export const getInterviewStatusLabel = (status: InterviewStatus | null) => {
  if (!status) return 'Unknown';
  return InterviewStatusLabels[status as InterviewStatus];
};

export const SurveyStatusLabels: Record<SurveyStatus, string> = {
  [SurveyStatus.DRAFT]: 'Draft',
  [SurveyStatus.LIVE]: 'Live',
  [SurveyStatus.COMPLETE]: 'Closed',
  [SurveyStatus.ARCHIVED]: 'Archived'
};

export const getSurveyStatusLabel = (status: SurveyStatus | null) => {
  if (!status) return 'Unknown';
  return SurveyStatusLabels[status as SurveyStatus];
};

export const orderedSurveyStatus: SurveyStatus[] = [
  SurveyStatus.DRAFT,
  SurveyStatus.LIVE,
  SurveyStatus.COMPLETE,
  SurveyStatus.ARCHIVED
];

export const InterviewDeviceSupportLabels: Record<
  InterviewDeviceSupport,
  string
> = {
  [InterviewDeviceSupport.ALL_DEVICES]: 'All Devices',
  [InterviewDeviceSupport.MOBILE_ONLY]: 'Mobile Only',
  [InterviewDeviceSupport.DESKTOP_ONLY]: 'Desktop Only'
};

export const getInterviewDeviceSupportLabel = (
  support: InterviewDeviceSupport | null | undefined
) => {
  if (support == null) return '-';
  return InterviewDeviceSupportLabels[support] ?? '-';
};

export const DeviceTypeLabels: Record<DeviceType, string> = {
  [DeviceType.DESKTOP]: 'Desktop',
  [DeviceType.MOBILE]: 'Mobile'
};

export const getDeviceTypeLabel = (
  device: DeviceType | number | null | undefined
) => {
  if (device == null) return '-';
  return DeviceTypeLabels[device as DeviceType] ?? '-';
};

export const ConversationRewardStatusMeta: Record<
  ConversationRewardStatus,
  { label: string; className: string }
> = {
  [ConversationRewardStatus.AWAITING_EMAIL]: {
    label: 'Awaiting Email',
    className: 'bg-muted text-zinc-800 border-border'
  },
  [ConversationRewardStatus.AWAITING_APPROVAL]: {
    label: 'Awaiting Approval',
    className: 'bg-orange-100 text-orange-800 border-orange-200'
  },
  [ConversationRewardStatus.SENT]: {
    label: 'Sent',
    className: 'bg-green-100 text-green-800 border-green-200'
  },
  [ConversationRewardStatus.REJECTED]: {
    label: 'Rejected',
    className: 'bg-red-100 text-red-800 border-red-200'
  }
};

/**
 * The reward statuses a claim can reach, in the order they are offered as
 * filters. A claim is recorded already awaiting approval, so AWAITING_EMAIL is
 * not among them.
 */
export const orderedConversationRewardStatuses: ConversationRewardStatus[] = [
  ConversationRewardStatus.AWAITING_APPROVAL,
  ConversationRewardStatus.SENT,
  ConversationRewardStatus.REJECTED
];

export const PLAN_LABELS: Record<Plan, string> = {
  [Plan.STARTER]: 'Starter',
  [Plan.PREMIUM]: 'Premium',
  [Plan.ENTERPRISE]: 'Enterprise'
};

export const SUBSCRIPTION_MANAGED_BY_LABELS: Record<
  SubscriptionManagedBy,
  string
> = {
  [SubscriptionManagedBy.NONE]: 'None',
  [SubscriptionManagedBy.MANUAL]: 'Manual',
  [SubscriptionManagedBy.STRIPE]: 'Stripe'
};

export const PanelCampaignStatusLabels: Record<PanelCampaignStatus, string> = {
  [PanelCampaignStatus.DRAFT]: 'Draft',
  [PanelCampaignStatus.ACTIVE]: 'Active',
  [PanelCampaignStatus.PAUSED]: 'Paused',
  [PanelCampaignStatus.COMPLETED]: 'Completed'
};

export const getPanelCampaignStatusLabel = (status: number | null) => {
  if (!status) return 'Unknown';
  return PanelCampaignStatusLabels[status as PanelCampaignStatus] || 'Unknown';
};

export const MediaTypeLabels: Partial<Record<MediaType, string>> = {
  [MediaType.IMAGE]: 'Image',
  [MediaType.VIDEO]: 'Video',
  [MediaType.IFRAME]: 'Iframe'
};

export const TopicArchetypeLabels = {
  [TopicArchetype.INQUISITOR]: 'The Curious Explorer',
  [TopicArchetype.PROCEDURALIST]: 'The Fact-Finder',
  [TopicArchetype.CHALLENGER]: 'The Challenger',
  [TopicArchetype.CONVERSATIONALIST]: 'The Friendly Conversationalist'
};

export const getTopicArchetypeLabel = (archetype: TopicArchetype) => {
  return TopicArchetypeLabels[archetype];
};
export const orderedTopicArchetypes = [
  TopicArchetype.INQUISITOR,
  TopicArchetype.PROCEDURALIST,
  TopicArchetype.CHALLENGER,
  TopicArchetype.CONVERSATIONALIST
];

export const CodeDataTypeMeta = {
  [CodeDataType.TEXT]: {
    label: 'Text',
    icon: TextIcon
  },
  [CodeDataType.BOOLEAN]: {
    label: 'Yes / No',
    icon: ToggleLeftIcon
  },
  [CodeDataType.NUMBER]: {
    label: 'Number (Integer)',
    icon: HashIcon
  },
  [CodeDataType.FLOAT]: {
    label: 'Number (Decimal)',
    icon: PercentIcon
  }
};

export const CodeSourceLabels = {
  [CodeSourceTypeEnum.PROMPT]: 'AI Generated',
  [CodeSourceTypeEnum.HUMAN]: 'Hand-Coded',
  [CodeSourceTypeEnum.SYSTEM]: 'System',
  [CodeSourceTypeEnum.URL_PARMS]: 'URL Parameters',
  [CodeSourceTypeEnum.RESPONDENT]: 'Profile Based',
  [CodeSourceTypeEnum.SCREENER]: 'Screener',
  [CodeSourceTypeEnum.SCREENER_QUESTION]: 'Screener Question',
  [CodeSourceTypeEnum.USER_EVAL]: 'User Evaluation',
  [CodeSourceTypeEnum.USER_EVAL_TAGGED]: 'User Evaluation (Tagged)',
  [CodeSourceTypeEnum.TOPIC]: 'Topic Response'
};

export const CodeSourceMeta = {
  [CodeSourceTypeEnum.PROMPT]: {
    label: 'AI Generated',
    icon: BrainIcon
  },
  [CodeSourceTypeEnum.HUMAN]: {
    label: 'Hand-Coded',
    icon: HandIcon
  },
  [CodeSourceTypeEnum.URL_PARMS]: {
    label: 'URL Parameters',
    icon: LinkIcon
  },
  [CodeSourceTypeEnum.RESPONDENT]: {
    label: 'Profile Based',
    icon: UserIcon
  },
  [CodeSourceTypeEnum.SCREENER]: {
    label: 'Screener',
    icon: ShieldIcon
  },
  [CodeSourceTypeEnum.SCREENER_QUESTION]: {
    label: 'Screener Question',
    icon: ShieldIcon
  },
  [CodeSourceTypeEnum.USER_EVAL]: {
    label: 'User Evaluation',
    icon: UserIcon
  },
  [CodeSourceTypeEnum.USER_EVAL_TAGGED]: {
    label: 'User Evaluation (Tagged)',
    icon: UserIcon
  },
  [CodeSourceTypeEnum.SYSTEM]: {
    label: 'System',
    icon: CogIcon
  },
  [CodeSourceTypeEnum.TOPIC]: {
    label: 'Topic Response',
    icon: MessageCircleQuestionIcon
  },
  [CodeSourceTypeEnum.REPORT]: {
    label: 'Report',
    icon: FileIcon
  }
};

export const ModalityMeta = {
  [InterviewModality.AUDIO]: {
    label: 'Audio',
    icon: MicIcon
  },
  [InterviewModality.VIDEO]: {
    label: 'Video',
    icon: VideoIcon
  },
  [InterviewModality.TEXT]: {
    label: 'Text',
    icon: SquarePenIcon
  },
  [InterviewModality.PHONE]: {
    label: 'Phone Call',
    icon: PhoneIcon
  },
  [InterviewModality.TEXT_VOICE]: {
    label: 'Voice + Text',
    icon: BookAudioIcon
  },
  [InterviewModality.ALL]: {
    label: 'All',
    icon: LayersIcon
  }
};

/** The order the modality pickers list modalities in. */
export const orderedModalities: InterviewModality[] = [
  InterviewModality.VIDEO,
  InterviewModality.AUDIO,
  InterviewModality.TEXT,
  InterviewModality.PHONE,
  InterviewModality.TEXT_VOICE,
  InterviewModality.ALL
];

/**
 * The lighter modality a respondent may drop to, when the interview offers one.
 * Which of these an interview can offer depends on the modality it runs.
 */
export const FallbackModalityMeta = {
  [InterviewFallbackModality.NONE]: {
    label: 'None',
    icon: BanIcon
  },
  [InterviewFallbackModality.AUDIO]: {
    label: 'Audio',
    icon: MicIcon
  },
  [InterviewFallbackModality.TEXT]: {
    label: 'Text',
    icon: SquarePenIcon
  },
  [InterviewFallbackModality.AUDIO_OR_TEXT]: {
    label: 'Audio or Text',
    icon: MessageCircleQuestionIcon
  }
};

export const LayoutMeta = {
  [InterviewLayout.SPEAKER]: {
    label: 'Speaker',
    icon: UserIcon
  },
  [InterviewLayout.STIMULUS]: {
    label: 'Stimulus',
    icon: MonitorPlayIcon
  },
  [InterviewLayout.UXR]: {
    label: 'UXR',
    icon: FlaskConicalIcon
  },
  [InterviewLayout.GUIDED]: {
    label: 'Guided',
    icon: CompassIcon
  }
};

/** The order the layout picker lists layouts in. */
export const orderedLayouts: InterviewLayout[] = [
  InterviewLayout.SPEAKER,
  InterviewLayout.STIMULUS,
  InterviewLayout.UXR,
  InterviewLayout.GUIDED
];

export const MobileCameraSourceMeta = {
  [MobileCameraSource.REAR]: { label: 'Rear Camera', icon: CameraIcon },
  [MobileCameraSource.FRONT]: { label: 'Front Camera', icon: ScanFaceIcon },
  [MobileCameraSource.EITHER]: { label: 'Either', icon: SwitchCameraIcon }
};

/** The order the Mobile Camera Source picker lists its options in. */
export const orderedMobileCameraSources: MobileCameraSource[] = [
  MobileCameraSource.REAR,
  MobileCameraSource.FRONT,
  MobileCameraSource.EITHER
];

export const PanelTypeLabels = {
  [PanelType.PROLIFIC]: 'Prolific',
  [PanelType.RESPONDENT]: 'Respondent',
  [PanelType.RESPONDENT_B2B]: 'Respondent B2B',
  [PanelType.RESPONDENT_B2C]: 'Respondent B2C',
  [PanelType.ENGAGE]: 'Engage',
  [PanelType.CUSTOM]: 'Custom'
};

export const getPanelTypeLabel = (panel: PanelType | number) => {
  return PanelTypeLabels[panel as PanelType] || 'Unknown';
};

export const GetPromptType = (
  topicType: TopicType,
  archetype?: TopicArchetype
): PromptType | null => {
  const surveyTypes = [
    TopicType.SURVEY_TEXT,
    TopicType.SURVEY_MULTI_CHOICE,
    TopicType.SURVEY_BOOLEAN,
    TopicType.SURVEY_DROPDOWN
  ];

  if (topicType === TopicType.VOICE) {
    if (archetype === TopicArchetype.INQUISITOR) {
      return PromptType.INQUISITOR;
    } else if (archetype === TopicArchetype.PROCEDURALIST) {
      return PromptType.PROCEDURALIST;
    } else if (archetype === TopicArchetype.CHALLENGER) {
      return PromptType.CHALLENGER;
    } else if (archetype === TopicArchetype.CONVERSATIONALIST) {
      return PromptType.CONVERSATIONALIST;
    }
  } else if (topicType === TopicType.QUESTION) {
    return PromptType.QUESTION;
  } else if (topicType === TopicType.STATEMENT) {
    return PromptType.STATEMENT;
  } else if (surveyTypes.includes(topicType)) {
    return PromptType.SURVEY;
  }
  return null;
};

export const InterviewLanguageMeta: Record<
  InterviewLanguage,
  { label: string; language: string; locale: string }
> = {
  [InterviewLanguage.EN_US]: {
    label: 'English (US)',
    language: 'en',
    locale: 'en-US'
  },
  [InterviewLanguage.ES_ES]: {
    label: 'Spanish (Spain)',
    language: 'es',
    locale: 'es-ES'
  },
  [InterviewLanguage.ES_MX]: {
    label: 'Spanish (Mexico)',
    language: 'es',
    locale: 'es-MX'
  },
  [InterviewLanguage.FR_FR]: {
    label: 'French (France)',
    language: 'fr',
    locale: 'fr-FR'
  },
  [InterviewLanguage.PT_BR]: {
    label: 'Portuguese (Brazil)',
    language: 'pt',
    locale: 'pt-BR'
  },
  [InterviewLanguage.HI_IN]: {
    label: 'Hindi (India)',
    language: 'hi',
    locale: 'hi-IN'
  },
  [InterviewLanguage.SW_KE]: {
    label: 'Swahili (Kenya)',
    language: 'sw',
    locale: 'sw-KE'
  },
  [InterviewLanguage.TL_PH]: {
    label: 'Tagalog (Philippines)',
    language: 'tl',
    locale: 'tl-PH'
  }
};

export const PromptTypeLabels = {
  [PromptType.INQUISITOR]: 'inquisitor',
  [PromptType.PROCEDURALIST]: 'proceduralist',
  [PromptType.CHALLENGER]: 'challenger',
  [PromptType.CONVERSATIONALIST]: 'conversationalist',
  [PromptType.QUESTION]: 'question',
  [PromptType.STATEMENT]: 'statement',
  [PromptType.SURVEY]: 'survey'
};

/**
 * How each report style presents itself to the person choosing one. The
 * description is what makes the choice: a style changes only how the report
 * reads, so the picker has to say what reading differently means for each.
 */
export const ReportStyleMeta = {
  [ReportStyle.STANDARD]: {
    label: 'Standard',
    description: 'Balanced narrative prose, a paragraph or two per section.',
    icon: AlignLeftIcon
  },
  [ReportStyle.EXECUTIVE]: {
    label: 'Executive',
    description:
      'Built to be skimmed — a clear takeaway per section, then concise bullets.',
    icon: ListIcon
  },
  [ReportStyle.NARRATIVE]: {
    label: 'Narrative',
    description:
      "Story-driven, carrying the findings through respondents' own words.",
    icon: BookOpenIcon
  },
  [ReportStyle.ACTIONABLE]: {
    label: 'Actionable',
    description:
      'Every finding carried through to what your team should do about it.',
    icon: TargetIcon
  }
};

/** Report styles in the order they are offered. */
export const orderedReportStyles: ReportStyle[] = [
  ReportStyle.STANDARD,
  ReportStyle.EXECUTIVE,
  ReportStyle.NARRATIVE,
  ReportStyle.ACTIONABLE
];

export const getReportStyleLabel = (style: ReportStyle): string =>
  ReportStyleMeta[style]?.label ?? '';
